import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ResponsiveModal } from '../controls/ResponsiveModal'

export interface VoiceSampleDialogProps {
  open: boolean
  /** `sheet` = phone bottom sheet, `dialog` = desktop dialog (`useModalVariant`). */
  variant: 'sheet' | 'dialog'
  /** Backdrop and Escape, which the host may refuse or ask about first. */
  onDismiss: () => void
  /** The `VoiceSamplePanel`. */
  children: ReactNode
}

/**
 * One size through every phase, so nothing jumps between them: a tall sheet on the phone, and on
 * the desktop a dialog wide enough for the panel's two columns (`@3xl/modal`, 48 rem). The height
 * is a height, not a limit: the panel's instrument takes its own from it and sizes the ring by it.
 */
export const VOICE_SAMPLE_SIZE = {
  sheet: 'overflow-hidden data-[side=bottom]:h-[92dvh]',
  dialog: 'h-[min(44rem,calc(100dvh-2rem))] w-full max-w-[min(54rem,calc(100%-2rem))]!',
}

/**
 * The voice sample as a modal (ADR-0062): a bottom sheet on the phone, a dialog on the desktop,
 * both around the same `VoiceSamplePanel`. The panel is handed in, so the host can let the one
 * component that follows the recorder's ticks render alone.
 */
export function VoiceSampleDialog({ open, variant, onDismiss, children }: VoiceSampleDialogProps) {
  const { t } = useTranslation()
  return (
    <ResponsiveModal
      open={open}
      onOpenChange={next => {
        if (!next) onDismiss()
      }}
      modal={{ variant, title: t('humint.voice.sample.title'), size: VOICE_SAMPLE_SIZE }}
    >
      {children}
    </ResponsiveModal>
  )
}
